import { Middleware } from 'redux';
import { BusinessError, NetworkError, QuotaExceededError } from '@mews-framework/errors';
import { AssetLoaderError } from '../assetLoader';
import { LoggerService, SentryReporterService } from '../services';

interface CreateErrorReporterMiddleware {
    loggerService?: LoggerService;
    isErrorWhitelisted?: <E>(error: E) => boolean;
    sentryReporterService?: SentryReporterService;
}

type ErrorReporterMiddleware = (props: CreateErrorReporterMiddleware) => Middleware;

const origin = 'ErrorReporterMiddleware';

const warningErrors = [AssetLoaderError, BusinessError, QuotaExceededError, NetworkError];

const isErrorWarning = (error: Error) => {
    let isWarning = false;

    warningErrors.forEach((warningErrorType) => {
        if (error instanceof warningErrorType || error.name === warningErrorType.name) {
            isWarning = true;
        }
    });

    return isWarning;
};

export const createErrorReporterMiddleware: ErrorReporterMiddleware =
    ({ isErrorWhitelisted, loggerService, sentryReporterService }) =>
    () =>
    (next) =>
    (action) => {
        try {
            return next(action);
        } catch (error) {
            if (isErrorWhitelisted && isErrorWhitelisted(error)) {
                return;
            }

            const extra = {
                origin,
                // `action` can be a function, so Sentry will serialize it as "[Function: <anonymous>]". This is not helpful, so let's try extracting the function name and partially serializing the function body.
                action,
                actionFunctionName: action.name,
                actionFunctionBody: (() => {
                    // Not everything in JavaScript has a `toString` method and even if it did, it's not 100% sure it won't crash, so let's wrap it in try-catch.
                    try {
                        return (
                            action
                                .toString()
                                // We don't want to serialize the whole function body, because it can be very long. Let's limit it.
                                .slice(0, 400)
                        );
                    } catch (e) {
                        return null;
                    }
                })(),
            };

            if (sentryReporterService) {
                const reportError = isErrorWarning(error)
                    ? sentryReporterService.logWarning
                    : sentryReporterService.logError;

                reportError({ error, extra });
            }

            if (loggerService) {
                loggerService.info('sentry-trace', 'Caught error in errorReporter', {
                    // `error` is sometimes not serializable, so let's extract the message and name manually.
                    error,
                    errorMessage: error?.message,
                    errorName: error?.name,
                    ...extra,
                });
            }

            window.console.error(error, { action });
        }
    };
